import { useCallback } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, call } from '../client';
import { qk } from '../keys';
import type { RuleCreate, StrategyCreate, StrategyUpdate, RuleUpdate, PreviewRequest, RetimeRequest } from '../schema';

export function useStrategies() {
  return useQuery({ queryKey: qk.strategies.list, queryFn: () => call(api.GET('/api/v1/strategies')) });
}

export function useStrategy(id: string | undefined) {
  return useQuery({
    queryKey: qk.strategies.detail(id ?? ''),
    queryFn: () => call(api.GET('/api/v1/strategies/{strategy_id}', { params: { path: { strategy_id: id! } } })),
    enabled: Boolean(id),
  });
}

/** Each condition and whether it currently holds on one instrument. */
export function useConditionStates(strategyId: string | undefined, instrumentId: string | undefined) {
  return useQuery({
    queryKey: qk.strategies.conditions(strategyId ?? '', instrumentId ?? ''),
    queryFn: () =>
      call(
        api.GET('/api/v1/strategies/{strategy_id}/instruments/{instrument_id}/conditions', {
          params: { path: { strategy_id: strategyId!, instrument_id: instrumentId! } },
        }),
      ),
    enabled: Boolean(strategyId && instrumentId),
  });
}

/**
 * The series for every rule of a saved strategy on a chart (one read per rule; the batched
 * chart read replaces this for the multi-chart view, §14.11).
 */
export function useRuleSeries(
  strategyId: string | undefined,
  ruleIds: string[],
  instrumentId: string | undefined,
  timeframe: string,
  opts?: { limit?: number; endAt?: string },
) {
  return useQueries({
    queries: ruleIds.map((ruleId) => ({
      queryKey: qk.strategies.series(strategyId ?? '', ruleId, instrumentId ?? '', timeframe, opts?.endAt),
      queryFn: () =>
        call(
          api.GET('/api/v1/strategies/{strategy_id}/rules/{rule_id}/series', {
            params: {
              path: { strategy_id: strategyId!, rule_id: ruleId },
              query: { instrument_id: instrumentId!, timeframe, limit: opts?.limit ?? 300, end_at: opts?.endAt },
            },
          }),
        ),
      enabled: Boolean(strategyId && instrumentId),
      staleTime: 60_000,
    })),
  });
}

export function useCandles(instrumentId: string | undefined, timeframe: string, limit = 300, endAt?: string, opts: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: qk.candles(instrumentId ?? '', timeframe, limit, endAt),
    queryFn: () =>
      call(
        api.GET('/api/v1/instruments/{instrument_id}/candles', {
          params: { path: { instrument_id: instrumentId! }, query: { timeframe, limit, end_at: endAt } },
        }),
      ),
    // `enabled: false` keeps the candles it has without asking again (a side panel slid shut).
    enabled: Boolean(instrumentId) && opts.enabled !== false,
    staleTime: 30_000,
  });
}

/**
 * A page of candles ending at a fixed instant, for a chart scrolling back (`useChartWindow`). Such
 * a page never changes, so it is kept for the session: returning to the chart costs nothing.
 */
export function useCandlePages(instrumentId: string | undefined, timeframe: string) {
  const qc = useQueryClient();
  return useCallback(
    (endAt: string, limit: number) =>
      qc
        .fetchQuery({
          queryKey: qk.candles(instrumentId ?? '', timeframe, limit, endAt),
          queryFn: () =>
            call(
              api.GET('/api/v1/instruments/{instrument_id}/candles', {
                params: { path: { instrument_id: instrumentId! }, query: { timeframe, limit, end_at: endAt } },
              }),
            ),
          staleTime: Infinity,
        })
        .then((r) => r.candles),
    [qc, instrumentId, timeframe],
  );
}

export function useMatchFrequency(id: string | undefined) {
  return useQuery({
    queryKey: qk.strategies.matchFrequency(id ?? ''),
    queryFn: () => call(api.GET('/api/v1/strategies/{strategy_id}/match-frequency', { params: { path: { strategy_id: id! } } })),
    enabled: Boolean(id),
  });
}

export function useApplications() {
  return useQuery({ queryKey: qk.applications, queryFn: () => call(api.GET('/api/v1/strategy-applications')) });
}

function useInvalidateStrategies() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: qk.strategies.all });
    void qc.invalidateQueries({ queryKey: qk.ranking });
    void qc.invalidateQueries({ queryKey: qk.applications });
  };
}

export function useCreateStrategy() {
  const done = useInvalidateStrategies();
  return useMutation({ mutationFn: (body: StrategyCreate) => call(api.POST('/api/v1/strategies', { body })), onSuccess: done });
}

export function useUpdateStrategy(id: string) {
  const done = useInvalidateStrategies();
  return useMutation({
    mutationFn: (body: StrategyUpdate) => call(api.PATCH('/api/v1/strategies/{strategy_id}', { params: { path: { strategy_id: id } }, body })),
    onSuccess: done,
  });
}

export function useDeleteStrategy() {
  const done = useInvalidateStrategies();
  return useMutation({
    mutationFn: (id: string) => call(api.DELETE('/api/v1/strategies/{strategy_id}', { params: { path: { strategy_id: id } } })),
    onSuccess: done,
  });
}

/** Several at once (`POST /strategies/delete`): one request for up to 200; ids already gone are skipped. */
export function useDeleteStrategies() {
  const done = useInvalidateStrategies();
  return useMutation({
    mutationFn: (ids: string[]) => call(api.POST('/api/v1/strategies/delete', { body: { strategy_ids: ids } })),
    onSuccess: done,
  });
}

export function useRepairStrategy(id: string) {
  const done = useInvalidateStrategies();
  return useMutation({
    mutationFn: () => call(api.POST('/api/v1/strategies/{strategy_id}/repair', { params: { path: { strategy_id: id } } })),
    onSuccess: done,
  });
}

export function useRetimeStrategy(id: string) {
  const done = useInvalidateStrategies();
  return useMutation({
    mutationFn: (body: RetimeRequest) =>
      call(api.POST('/api/v1/strategies/{strategy_id}/retime', { params: { path: { strategy_id: id } }, body })),
    onSuccess: done,
  });
}

/** The raw rule writes the builder composes into one save. */
export const ruleApi = {
  createBatch: (strategyId: string, rules: RuleCreate[]) =>
    call(api.POST('/api/v1/strategies/{strategy_id}/rules/batch', { params: { path: { strategy_id: strategyId } }, body: { rules } })),
  update: (strategyId: string, ruleId: string, body: RuleUpdate) =>
    call(api.PATCH('/api/v1/strategies/{strategy_id}/rules/{rule_id}', { params: { path: { strategy_id: strategyId, rule_id: ruleId } }, body })),
  remove: (strategyId: string, ruleId: string) =>
    call(api.DELETE('/api/v1/strategies/{strategy_id}/rules/{rule_id}', { params: { path: { strategy_id: strategyId, rule_id: ruleId } } })),
};

export const previewStrategy = (body: PreviewRequest) => call(api.POST('/api/v1/strategies/preview', { body }));

export function useInvalidateAfterSave() {
  return useInvalidateStrategies();
}

// ---- AI (ai_features.md: draft-from-description, "Review with AI") ---------------------------

export function useAiDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (description: string) => call(api.POST('/api/v1/ai/strategy-draft', { body: { description } })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['testing'] }),
  });
}

/** The stored review, if any (a 404 means none has been requested yet). */
export function useAiReview(strategyId: string | undefined) {
  return useQuery({
    queryKey: ['ai-review', strategyId ?? ''],
    // No review yet answers with no body; React Query needs a value, so that is null.
    queryFn: async () => (await call(api.GET('/api/v1/ai/strategies/{strategy_id}/review', { params: { path: { strategy_id: strategyId! } } }))) ?? null,
    enabled: Boolean(strategyId),
    retry: false,
  });
}

export function useRequestAiReview(strategyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => call(api.POST('/api/v1/ai/strategies/{strategy_id}/review', { params: { path: { strategy_id: strategyId } } })),
    onSuccess: (out) => {
      qc.setQueryData(['ai-review', strategyId], out);
      void qc.invalidateQueries({ queryKey: ['testing'] });
    },
  });
}
